import { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { FaCopy } from "react-icons/fa";
import { useSelector } from "react-redux";
import { FranchisePaymentTransactionInterface } from "../../interfaces/franchise-payment.interface";
import { RootState } from "../../redux/store";
import { get, post } from "../../redux/ApiHelper";
import TableComponent from "../../components/table/TableComponent";
import { Column } from "../../interfaces/table.interface";
import { errorToast, successToast } from "../../utils/toast.util";
import { formatDateTime } from "../../utils/date&time.util";

const FranchisePayment = () => {
  const [paymentsList, setPaymentsList] = useState<
    FranchisePaymentTransactionInterface[]
  >([]);
  const [filteredPaymentsList, setFilteredPaymentsList] = useState<
    FranchisePaymentTransactionInterface[]
  >([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [verifyingMap, setVerifyingMap] = useState<Record<string, boolean>>({});
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("type", String(signedAdmin.type));
    queryParams.set("adminId", String(signedAdmin.id));
    get(`/admin/payment/franchise-payments?${queryParams.toString()}`).then(
      (res) => {
        setPaymentsList(res);
        setFilteredPaymentsList(res);
      }
    );
  }, []);

  //   const handleSearch = (query: string) => {
  //     setSearchQuery(query);
  //     const lowerQuery = query.toLowerCase();

  //     const filteredList = paymentsList?.filter((payment) => {
  //       const createdByName = payment.createdBy?.name?.toLowerCase() || "";
  //       const role = payment.role?.toLowerCase() || "";
  //       const transactionId = payment.transactionId?.toLowerCase() || "";
  //       const externalTransactionId =
  //         payment.externalTransactionId?.toLowerCase() || "";
  //       const paymentDate = formatDateTime(payment?.createdAt);

  //       return (
  //         payment._id.includes(lowerQuery) ||
  //         payment.amount.toString().includes(lowerQuery) ||
  //         payment.currency.toLowerCase().includes(lowerQuery) ||
  //         role.includes(lowerQuery) ||
  //         createdByName.includes(lowerQuery) ||
  //         transactionId.includes(lowerQuery) ||
  //         paymentDate.toLowerCase().includes(lowerQuery) ||
  //         externalTransactionId.includes(lowerQuery)
  //       );
  //     });

  //     setFilteredPaymentsList(filteredList);
  //   };

  const columns: Column[] = [
    { select: "_id", header: "Payment ID", align: "left" },
    {
      header: "Admin",
      render: (_value: any, row: FranchisePaymentTransactionInterface) => {
        return (
          <div className="flex flex-col">
            <p>{row.adminId.name}</p>
            <p>{row.adminId.email}</p>
          </div>
        );
      },
    },
    {
      header: "amount",
      render: (_value: any, row: FranchisePaymentTransactionInterface) => {
        const collectedAmount = row.amount;
        const gatewayCharge = (collectedAmount * 2) / 100;
        const platformFee = (collectedAmount * 20) / 100;
        const gstOnPlatformFee = (platformFee * 18) / 100;
        const amountToBeTransferred =
          collectedAmount - gstOnPlatformFee - gatewayCharge;

        // Formatter for INR with 2 decimals
        const formatINR = (amount: number) =>
          amount.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

        return (
          <div className="space-y-1">
            {signedAdmin.type === "franchise" ? (
              <div>
                <p className="whitespace-nowrap">
                  Collected amount: {formatINR(collectedAmount)}
                </p>

                <p className="whitespace-nowrap">
                  Gateway Charge (2%): {formatINR(gatewayCharge)}
                </p>
                <p className="whitespace-nowrap">
                  Platform Fee (20%): {formatINR(platformFee)}
                </p>
                <p className="whitespace-nowrap">
                  GST on Platform Fee (18%): {formatINR(gstOnPlatformFee)}
                </p>
                <p className="whitespace-nowrap font-semibold text-green-600">
                  Amount to be transferred: {formatINR(amountToBeTransferred)}
                </p>
              </div>
            ) : signedAdmin.type === "employee" ? (
              <div>{collectedAmount}</div>
            ) : null}
          </div>
        );
      },
    },
    //   { select: "currency", header: "Currency" },
    {
      header: "Status",
      render: (_value: any, row: FranchisePaymentTransactionInterface) => {
        const isCompleted = row?.status === "Completed";

        return (
          <div className="flex items-center">
            <span
              className={`status-badge ${
                isCompleted
                  ? "bg-green-500"
                  : row?.status === "Pending"
                  ? "bg-yellow-500"
                  : "bg-red-500"
              } text-white px-2 py-1 rounded`}
            >
              {row?.status}
            </span>
          </div>
        );
      },
    },
    {
      header: "Action",
      render: (_value: any, row: FranchisePaymentTransactionInterface) => {
        const isVerifying = verifyingMap[row._id];

        const handleVerify = async () => {
          setVerifyingMap((prev) => ({ ...prev, [row._id]: true }));
          post(`/admin/payment/payment-franchise/order-validate/${row._id}`, {
            role: signedAdmin.role,
            adminId: row.adminId,
            type: signedAdmin.type,
          })
            .then((response) => {
              const { newPaymentData, message } = response;

              const updatedList = paymentsList.map((payment) =>
                payment._id === row._id
                  ? { ...payment, ...newPaymentData }
                  : payment
              );
              setPaymentsList(updatedList);
              setFilteredPaymentsList(updatedList);
              successToast(message || "Payment successfully updated!");
            })
            .catch((e) =>
              errorToast(
                e.message || "Failed to verify payment. Please try again."
              )
            )
            .finally(() =>
              setVerifyingMap((prev) => ({ ...prev, [row._id]: false }))
            );
        };

        const isCompleted = row?.status === "Completed";

        return (
          <div className="flex items-center">
            {!isCompleted && (
              <div className="ml-2">
                <Tooltip
                  title="Click to verify the payment if it seems stuck. This will validate the payment status."
                  placement="top"
                >
                  <button
                    className={`text-white px-3 py-1 rounded ${
                      isVerifying
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600"
                    }`}
                    onClick={handleVerify}
                    disabled={isVerifying}
                    aria-label="Verify Payment"
                  >
                    {isVerifying ? "Verifying..." : "Verify"}
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
    //   {
    //     header: "Transaction ID",
    //     render: (_value: any, row: FranchisePaymentTransactionInterface) =>
    //       row?.transactionId || "N/A",
    //   },
    //   {
    //     header: "External Transaction ID",
    //     render: (_value: any, row: FranchisePaymentTransactionInterface) => {
    //       const transactionId = row?.externalTransactionId || "N/A";

    //       if (transactionId === "N/A") {
    //         return transactionId;
    //       }

    //       const handleCopy = () => {
    //         navigator.clipboard.writeText(transactionId);
    //         successToast("Transaction ID copied to clipboard!");
    //       };

    //       return (
    //         <div style={{ display: "flex", alignItems: "center" }}>
    //           <span style={{ marginRight: 8 }}>
    //             {transactionId.slice(0, 10)}...
    //           </span>
    //           <Tooltip title="Copy Transaction ID">
    //             <IconButton
    //               onClick={handleCopy}
    //               size="small"
    //               aria-label="Copy Transaction ID"
    //             >
    //               <FaCopy />
    //             </IconButton>
    //           </Tooltip>
    //         </div>
    //       );
    //     },
    //   },
    {
      header: "Date & Time",
      render: (_value: any, row: FranchisePaymentTransactionInterface) => (
        <p className="whitespace-nowrap">{formatDateTime(row?.updatedAt)}</p>
      ),
    },
    //   {
    //     header: "amount breakup",
    //     render: (_value: any, row: FranchisePaymentTransactionInterface) => {
    //       const collectedAmount = row.amount;
    //       const gatewayCharge = (collectedAmount * 2) / 100;
    //       const platformFee = (collectedAmount * 20) / 100;
    //       const gstOnPlatformFee = (platformFee * 18) / 100;
    //       const amountToBeTransferred =
    //         collectedAmount - gstOnPlatformFee - gatewayCharge;

    //       // Formatter for INR with 2 decimals
    //       const formatINR = (amount: number) =>
    //         amount.toLocaleString("en-IN", {
    //           style: "currency",
    //           currency: "INR",
    //           minimumFractionDigits: 2,
    //           maximumFractionDigits: 2,
    //         });

    //       return (
    //         <div className="space-y-1">
    //           <div>
    //             <p className="whitespace-nowrap">
    //               Collected amount: {formatINR(collectedAmount)}
    //             </p>

    //             <p className="whitespace-nowrap">
    //               Gateway Charge (2%): {formatINR(gatewayCharge)}
    //             </p>
    //             <p className="whitespace-nowrap">
    //               Platform Fee (20%): {formatINR(platformFee)}
    //             </p>
    //             <p className="whitespace-nowrap">
    //               GST on Platform Fee (18%): {formatINR(gstOnPlatformFee)}
    //             </p>
    //             <p className="whitespace-nowrap font-semibold text-green-600">
    //               Amount to be transferred: {formatINR(amountToBeTransferred)}
    //             </p>
    //           </div>
    //         </div>
    //       );
    //     },
    //   },
  ];

  return (
    <div className="paymentsPage table-container">
      <div className="title">Payments</div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search payments"
          value={searchQuery}
          //   onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <TableComponent
        columns={columns.filter((item) => {
          if (
            signedAdmin.type === "franchise" &&
            (item.header === "Action" || item.header === "amount breakup")
          ) {
            return false;
          } else {
            return true;
          }
        })}
        data={filteredPaymentsList}
      />
    </div>
  );
};

export default FranchisePayment;
